import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCompactDisc,
  faListCheck,
  faRightFromBracket,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import './css/design-system.css';
import './css/layout.css';
import { MusicPlayerProvider } from './music_player_context';

const MusicShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('walmates-token');
  const userId = localStorage.getItem('walmates-user_id');
  const [sessionState, setSessionState] = useState(token && userId ? 'checking' : 'signed-out');
  const [showSignOut, setShowSignOut] = useState(false);
  const loginName = localStorage.getItem('walmates-login_id') || 'You';

  useEffect(() => {
    const storedToken = localStorage.getItem('walmates-token');
    const storedUserId = localStorage.getItem('walmates-user_id');
    if (!storedToken || !storedUserId) {
      setSessionState('signed-out');
      return undefined;
    }

    let cancelled = false;
    fetch('/api/session/persist', {
      method: 'POST',
      headers: { Authorization: `Bearer ${storedToken}` },
    }).then(async (response) => {
      if (cancelled) return;
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('walmates-token');
        localStorage.removeItem('walmates-user_id');
        localStorage.removeItem('walmates-login_id');
        setSessionState('signed-out');
        navigate('/login', { replace: true });
        return;
      }
      if (response.ok) {
        const session = await response.json();
        if (session?.token) localStorage.setItem('walmates-token', session.token);
        if (session?.id != null) localStorage.setItem('walmates-user_id', String(session.id));
        if (session?.login_id) localStorage.setItem('walmates-login_id', session.login_id);
      }
      setSessionState('ready');
    }).catch(() => {
      // Keep a saved session usable during transient outages and offline use.
      if (!cancelled) setSessionState('ready');
    });

    return () => { cancelled = true; };
  }, [navigate]);

  if (!token || !userId || sessionState === 'signed-out') {
    return <Navigate to="/login" replace />;
  }
  if (sessionState === 'checking') {
    return <div className="session-checking" role="status">Checking your session…</div>;
  }

  const apps = [
    { path: '/music', label: 'Music', icon: faCompactDisc, tone: 'music' },
    { path: '/vault', label: 'Vault', icon: faShieldHalved, tone: 'vault' },
    { path: '/todo', label: 'Todo', icon: faListCheck, tone: 'todo' },
  ];
  const activePath = location.pathname;

  const signOut = () => {
    localStorage.removeItem('walmates-token');
    localStorage.removeItem('walmates-user_id');
    localStorage.removeItem('walmates-login_id');
    navigate('/login', { replace: true });
  };

  return (
    <MusicPlayerProvider>
      <header className="app-switcher-header">
        <nav className="app-switcher-links" aria-label="Apps">
          {apps.filter((app) => app.path !== activePath).map((app) => (
            <button
              type="button"
              key={app.path}
              className={`app-switcher-link is-${app.tone}`}
              onClick={() => navigate(app.path)}
            >
              <FontAwesomeIcon icon={app.icon} />
              <span>{app.label}</span>
            </button>
          ))}
        </nav>
        <button type="button" className="app-switcher-signout" onClick={() => setShowSignOut(true)}>
          <FontAwesomeIcon icon={faRightFromBracket} />
          <span>Sign out</span>
        </button>
      </header>
      {showSignOut && (
        <div className="signout-confirm-backdrop" role="presentation" onClick={() => setShowSignOut(false)}>
          <section className="signout-confirm" role="dialog" aria-modal="true" aria-labelledby="signout-confirm-title" onClick={(event) => event.stopPropagation()}>
            <h2 id="signout-confirm-title">{loginName}</h2>
            <p>Do you want to sign out?</p>
            <div className="signout-confirm-actions">
              <button type="button" onClick={() => setShowSignOut(false)}>Cancel</button>
              <button type="button" className="is-danger" onClick={signOut}>Yes</button>
            </div>
          </section>
        </div>
      )}
      <Outlet />
    </MusicPlayerProvider>
  );
};

export default MusicShell;
